import React, { useMemo, useState } from "react";
import { Box, Button, Flex, Heading } from "@telia/teddy";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { type FeedbackEntry } from "./feedbackTypes";
import "./ReviewChart.less";

const buildChartData = (entries: FeedbackEntry[], groupBy: "day" | "week" | "month") => {
    const reviews = entries.filter((e) => e.feedbackType === "review" && e.score !== null);
    const buckets: Record<string, { sum: number; count: number }> = {};

    for (const entry of reviews) {
        const d = new Date(entry.createdAt);
        let key: string;
        if (groupBy === "month") {
            key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        } else if (groupBy === "week") {
            const dayOfWeek = d.getDay();
            const monday = new Date(d);
            monday.setDate(d.getDate() - ((dayOfWeek + 6) % 7));
            key = `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, "0")}-${String(monday.getDate()).padStart(2, "0")}`;
        } else {
            key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        }
        buckets[key] ??= { sum: 0, count: 0 };
        buckets[key].sum += entry.score!;
        buckets[key].count += 1;
    }

    return Object.entries(buckets)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, { sum, count }]) => ({
            date,
            avgScore: Math.round((sum / count) * 10) / 10,
            count,
        }));
};

type Props = {
    feedback: FeedbackEntry[];
};

const ReviewChart: React.FC<Props> = ({ feedback }) => {
    const [chartGroupBy, setChartGroupBy] = useState<"day" | "week" | "month">("week");
    const chartData = useMemo(() => buildChartData(feedback, chartGroupBy), [feedback, chartGroupBy]);

    if (chartData.length === 0) return null;

    return (
        <Box className="review-chart">
            <Flex direction="row" justify="space-between" className="review-chart__header">
                <Heading mb="100" as="h3" variant="title-100">
                    Snittkarakter over tid
                </Heading>
                <Flex direction="row" gap="200">
                    {(["day", "week", "month"] as const).map((g) => (
                        <Button
                            key={g}
                            variant={chartGroupBy === g ? "primary" : "secondary"}
                            size="sm"
                            onClick={() => setChartGroupBy(g)}
                        >
                            {{ day: "Dag", week: "Uke", month: "Måned" }[g]}
                        </Button>
                    ))}
                </Flex>
            </Flex>
            <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--teddy-theme-color-border-weak)" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 12 }} />
                    <Tooltip
                        content={({ active, payload }) => {
                            if (!active || !payload?.length) return null;
                            const { date, avgScore, count } = payload[0].payload;
                            return (
                                <div className="review-chart__tooltip">
                                    <div className="review-chart__tooltip-date">{date}</div>
                                    <div>Snittkarakter: {avgScore} / 5</div>
                                    <div className="review-chart__tooltip-count">Antall vurderinger: {count}</div>
                                </div>
                            );
                        }}
                    />
                    <Line
                        type="monotone"
                        dataKey="avgScore"
                        stroke="var(--teddy-color-purple-700)"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </Box>
    );
};

export default ReviewChart;
